import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config/config";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { normalizeRole } from "../utils/roleMap";

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = "Experts4Europe | Login";
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/login`, { email, password });
      const user = response.data.user;
      const role = normalizeRole(user.role);

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("userid", user.userid);
      localStorage.setItem("role", role);

      if (role === "admin") navigate("/dashboard/admin");
      else if (role === "applicant") navigate("/dashboard/applicant");
      else if (role === "employer") navigate("/dashboard/employer");
      else if (role === "press") navigate("/dashboard/press");
      else setErrorMsg("Unknown user role");
    } catch (error) {
      setErrorMsg(error.response?.data?.error || "Server not reachable");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cockpit-bg flex min-h-screen items-center px-4 py-10 text-slate-100">
      <div className="mx-auto grid w-full max-w-[1120px] gap-6 lg:grid-cols-[1fr_440px]">
        <section className="glass-panel rounded-3xl p-8">
          <Badge className="mb-4 bg-cyan-500/20 text-cyan-100 ring-1 ring-cyan-300/40" variant="outline">
            Experts4Europe Access
          </Badge>
          <h1 className="font-display text-5xl font-bold leading-tight">Control your recruiting operations from one cockpit.</h1>
          <p className="mt-4 max-w-xl text-slate-300">
            Secure login for admins, employers, applicants, and press users. Real-time modules, role-based dashboards,
            and cross-Europe workflows.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white/5 p-4 text-sm text-slate-200">Role-based routing</div>
            <div className="rounded-xl bg-white/5 p-4 text-sm text-slate-200">Centralized user context</div>
            <div className="rounded-xl bg-white/5 p-4 text-sm text-slate-200">Ready for DB split</div>
          </div>
        </section>

        <Card className="card-e4e rounded-3xl">
          <CardHeader>
            <CardTitle className="font-display text-3xl text-white">Sign in</CardTitle>
            <CardDescription className="text-slate-300">Use your Experts4Europe account credentials.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/20 bg-slate-950/40 text-white placeholder:text-slate-400"
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/20 bg-slate-950/40 text-white placeholder:text-slate-400"
                required
              />

              {errorMsg ? <p className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-2 text-sm text-rose-200">{errorMsg}</p> : null}

              <Button type="submit" className="w-full bg-cyan-500 text-slate-950 hover:bg-cyan-300" disabled={isLoading}>
                {isLoading ? "Signing in..." : "Login"}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full border-white/20 bg-transparent text-slate-100 hover:bg-white/10"
                onClick={() => navigate("/")}
              >
                Back to Homepage
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
